import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { studentApi } from "@/lib/student-auth";
import { mediaSrc } from "@/lib/media";

export const OnboardingModal = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    studentApi
      .get("/student/onboarding")
      .then((r) => {
        if (r.data.should_show) {
          setData(r.data.video);
          setOpen(true);
        }
      })
      .catch(() => {});
  }, []);

  const close = async () => {
    setOpen(false);
    try {
      await studentApi.post("/student/onboarding/dismiss", { hide_forever: dontShow });
    } catch {
      /* non-blocking */
    }
  };

  if (!data) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="onboarding-overlay"
          className="fixed inset-0 z-[80] grid place-items-center bg-[#020617]/80 backdrop-blur-sm px-4 py-8 overflow-y-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            data-testid="onboarding-modal"
            className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#0F172A] text-white overflow-hidden shadow-[0_40px_120px_-40px_rgba(6,182,212,0.5)]"
          >
            <div className="flex items-start justify-between gap-3 p-5 pb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#06B6D4] font-semibold">
                  Welcome aboard
                </div>
                <h2 className="mt-2 font-display text-xl sm:text-2xl font-semibold tracking-tight">
                  {data.title}
                </h2>
                {data.description && (
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{data.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                data-testid="onboarding-close"
                className="h-8 w-8 shrink-0 grid place-items-center rounded-full border border-white/10 text-slate-300 hover:border-[#06B6D4]/60 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5">
              <div className="aspect-video rounded-2xl overflow-hidden bg-black relative">
                {playing ? (
                  <iframe
                    src={data.embed_url}
                    title={data.title}
                    allow="autoplay; fullscreen; encrypted-media"
                    allowFullScreen
                    className="h-full w-full"
                    data-testid="onboarding-player"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    data-testid="onboarding-play"
                    className="group h-full w-full relative"
                  >
                    {data.cover ? (
                      <img
                        src={mediaSrc(data.cover)}
                        alt={data.title}
                        data-testid="onboarding-cover"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#06B6D4]/30" />
                    )}
                    <span className="absolute inset-0 grid place-items-center bg-[#020617]/35 group-hover:bg-[#020617]/20 transition-colors">
                      <span className="h-16 w-16 grid place-items-center rounded-full bg-white/95 text-[#0F172A] shadow-xl group-hover:scale-105 transition-transform">
                        <Play className="h-6 w-6 translate-x-[1px]" />
                      </span>
                    </span>
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5">
              <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dontShow}
                  onChange={(e) => setDontShow(e.target.checked)}
                  data-testid="onboarding-dont-show"
                  className="h-3.5 w-3.5"
                />
                Don't show me again
              </label>
              <div className="flex items-center gap-2">
                {!playing && (
                  <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    data-testid="onboarding-watch"
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2.5 text-xs font-semibold hover:border-[#06B6D4]/60 transition-colors"
                  >
                    <Play className="h-3.5 w-3.5" /> Watch video
                  </button>
                )}
                <button
                  type="button"
                  onClick={close}
                  data-testid="onboarding-got-it"
                  className="inline-flex items-center rounded-full bg-white text-[#0F172A] px-5 py-2.5 text-xs font-semibold hover:bg-slate-100 transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
